import React, {ReactNode} from "react";
import {Box, ButtonBase, Skeleton, Stack, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import WorkHistoryRoundedIcon from "@mui/icons-material/WorkHistoryRounded";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import {Person, PersonSummary} from "arteco-api-client-ts";
import {Link as RouterLink} from "react-router-dom";
import dayjs from "dayjs";
import {euros, fullName, Panel, PersonAvatar, SoftChip, StatGrid, StatTile} from "../../../../components/kit";
import {dateToString} from "../../../../components/utils/dates";

/**
 * Cabecera de la ficha de persona (#13886): quién es, si está de alta, sus cifras de un vistazo
 * (antigüedad, vacaciones, salario, última autoevaluación) y los accesos a sus secciones.
 */

/** «Nombre Apellidos», con el formato antiguo «Apellidos, Nombre» si es lo único que hay. */
export const personName = (p?: { name?: string, surnames?: string, surnamesAndName?: string }) =>
	fullName(p) || p?.surnamesAndName || "";

/** «4 años y 3 meses», «8 meses», «menos de un mes». */
export const seniority = (hireDate?: string) => {
	if (!hireDate) {
		return "—";
	}
	const months = dayjs().diff(dayjs(hireDate), "month");
	if (months < 1) {
		return "menos de un mes";
	}
	const y = Math.floor(months / 12);
	const m = months % 12;
	const ys = y === 1 ? "1 año" : `${y} años`;
	const ms = m === 1 ? "1 mes" : `${m} meses`;
	return y === 0 ? ms : m === 0 ? ys : `${ys} y ${ms}`;
};

export interface PersonSection {
	label: string;
	to: string;
	icon: ReactNode;
}

/** Las secciones de una persona, en el orden en que más se usan. */
export const personSections = (id: number): PersonSection[] => [
	{label: "Salarios", to: `/app/admin/employee/person/${id}/salary`, icon: <EuroRoundedIcon/>},
	{label: "Ausencias", to: `/app/admin/employee/vacations?personId=${id}`, icon: <BeachAccessRoundedIcon/>},
	{label: "Archivos", to: `/app/admin/employee/person/${id}/document`, icon: <FolderRoundedIcon/>},
	{label: "Entrevistas", to: `/app/admin/employee/person/${id}/interview`, icon: <ForumRoundedIcon/>},
	{label: "Autoevaluaciones", to: `/app/admin/employee/person/${id}/evaluation`, icon: <FactCheckRoundedIcon/>},
	{label: "Valoración de clientes", to: `/app/admin/employee/person/${id}/support`, icon: <StarRateRoundedIcon/>},
	{label: "Eventos", to: `/app/admin/employee/person/${id}/event`, icon: <EventNoteRoundedIcon/>},
	{label: "Unidad", to: `/app/admin/employee/person/${id}/organization-unit`, icon: <AccountTreeRoundedIcon/>},
];

/** Rejilla de accesos: iconos tintados, tres por fila en móvil y ocho en escritorio. */
export const SectionLinks = ({personId}: { personId: number }) => {
	const theme = useTheme();
	return (
		<Box sx={{display: "grid", gridTemplateColumns: {xs: "repeat(3, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", lg: "repeat(8, minmax(0, 1fr))"}, gap: 1.5}}>
			{personSections(personId).map(s => (
				<ButtonBase key={s.label} component={RouterLink} to={s.to} sx={{
					flexDirection: "column", gap: 1, py: 1.75, px: 1, borderRadius: 3, border: "1px solid", borderColor: "divider",
					bgcolor: "background.paper", transition: "border-color .15s", "&:hover": {borderColor: "primary.main"},
				}}>
					<Box sx={{
						width: 36, height: 36, borderRadius: 2, display: "grid", placeItems: "center",
						bgcolor: alpha(theme.palette.primary.main, 0.1), color: "primary.main",
					}}>{s.icon}</Box>
					<Typography variant="caption" fontWeight={600} textAlign="center" sx={{lineHeight: 1.2}}>{s.label}</Typography>
				</ButtonBase>
			))}
		</Box>
	);
};

interface PersonOverviewProps {
	person?: Person;
	summary?: PersonSummary;
	photoUrl?: string;
}

/** Tarjeta de la persona: foto, nombre, presentación, estado y datos clave, y sus cifras debajo. */
export const PersonOverview = ({person, summary, photoUrl}: PersonOverviewProps) => {
	if (!person) {
		return <Skeleton variant="rounded" height={140} sx={{mb: 3}}/>;
	}
	const name = personName(person);
	const lastEval = summary?.evaluation?.evaluationDate;
	const evalOld = lastEval ? dayjs(lastEval).isBefore(dayjs().subtract(1, "month")) : false;
	return (
		<Box sx={{mb: 3}}>
			<Panel sx={{mb: 3}}>
				<Stack direction={{xs: "column", sm: "row"}} spacing={2.5} alignItems={{sm: "center"}}>
					<PersonAvatar name={name} src={photoUrl || undefined} size={72}/>
					<Box sx={{minWidth: 0, flexGrow: 1}}>
						<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
							<Typography variant="h6" fontWeight={700}>{name}</Typography>
							{person.dismissed
								? <SoftChip tone="error" label={`De baja desde el ${dateToString(person.dismissed)}`}/>
								: <SoftChip tone="success" label="En plantilla"/>}
							{person.lastUnit?.name && <SoftChip tone="info" label={person.lastUnit.name}/>}
						</Stack>
						{person.coverPhrase && (
							<Typography variant="body2" color="text.secondary" sx={{mt: 0.25}}>{person.coverPhrase}</Typography>
						)}
						<Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap sx={{mt: 1, color: "text.secondary"}}>
							{person.email && <Typography variant="caption">{person.email}</Typography>}
							{person.telephone && <Typography variant="caption">{person.telephone}</Typography>}
							{person.workCenter?.name && <Typography variant="caption">{person.workCenter.name}</Typography>}
							{person.birth && (
								<Stack direction="row" spacing={0.5} alignItems="center">
									<CakeRoundedIcon sx={{fontSize: 14}}/>
									<Typography variant="caption">{dayjs(person.birth).format("D [de] MMMM")}</Typography>
								</Stack>
							)}
						</Stack>
					</Box>
				</Stack>
			</Panel>

			<StatGrid>
				<StatTile icon={<WorkHistoryRoundedIcon fontSize="small"/>} tone="info" label="Antigüedad"
						  value={<Box component="span" sx={{fontSize: "1.1rem"}}>{seniority(person.hireDate)}</Box>}
						  hint={<Typography variant="caption" color="text.secondary">Alta {dateToString(person.hireDate)}</Typography>}/>
				<StatTile icon={<BeachAccessRoundedIcon fontSize="small"/>} tone="info" label="Vacaciones del año"
						  loading={!summary}
						  value={summary ? <>{summary.takenVacations ?? 0}<Box component="span" sx={{fontSize: "0.9rem", color: "text.secondary", fontWeight: 600}}> de {summary.allowedVacations ?? 0}</Box></> : undefined}/>
				<StatTile icon={<EuroRoundedIcon fontSize="small"/>} tone="success" label="Salario anual" loading={!summary}
						  value={summary ? <Box component="span" sx={{fontSize: "1.1rem"}}>{euros(summary.salary?.annualSalary, 0)}</Box> : undefined}
						  hint={summary?.lastSalaryUpdate &&
							  <Typography variant="caption" color="text.secondary">Revisado el {dateToString(summary.lastSalaryUpdate)}</Typography>}/>
				<StatTile icon={<FactCheckRoundedIcon fontSize="small"/>} tone={!lastEval || evalOld ? "warning" : "success"}
						  label="Última autoevaluación" loading={!summary}
						  value={summary ? <Box component="span" sx={{fontSize: "1.1rem"}}>{lastEval ? dateToString(lastEval) : "Nunca"}</Box> : undefined}/>
			</StatGrid>

			{person.id && (
				<Panel title="Secciones" subtitle="Todo lo que hay de esta persona.">
					<SectionLinks personId={person.id}/>
				</Panel>
			)}
		</Box>
	);
};
